// src/features/auth/mfa-dialog.tsx
import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { EnrollResponse } from '@/api/schemas/auth';

interface MfaDialogProps {
  open: boolean;
  mode: 'enroll' | 'verify';
  enrollData: EnrollResponse | null;
  pending?: boolean;
  error?: string | null;
  onSubmit: (code: string) => void;
  onOpenChange: (open: boolean) => void;
}

export function MfaDialog({
  open,
  mode,
  enrollData,
  pending = false,
  error,
  onSubmit,
  onOpenChange,
}: MfaDialogProps) {
  const [code, setCode] = useState('');
  useEffect(() => {
    if (!open) setCode('');
  }, [open]);

  const codeOk = /^\d{6}$/.test(code);
  const title = mode === 'enroll' ? 'Configura tu autenticador' : 'Verificación en dos pasos';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-surface p-5">
          <Dialog.Title className="text-lg font-semibold text-text-primary">{title}</Dialog.Title>

          {mode === 'enroll' ? (
            <>
              <Dialog.Description className="mt-1 text-sm text-text-muted">
                Aún no tienes un autenticador configurado. Sigue estos pasos:
              </Dialog.Description>
              <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-text-secondary">
                <li>Instala una app de autenticación (Google Authenticator, Authy o 1Password).</li>
                <li>Escanea el código QR (o usa la clave manual).</li>
                <li>Ingresa el código de 6 dígitos que muestra la app.</li>
              </ol>
              {enrollData && (
                <div className="mt-4 flex items-center gap-4 rounded-lg border border-border bg-graphite p-4">
                  <img
                    src={enrollData.qrDataUrl}
                    alt="Código QR de registro MFA"
                    className="h-32 w-32 rounded bg-white p-1"
                  />
                  <div className="text-xs text-text-muted">
                    <div className="mb-1 uppercase tracking-wide">Clave manual</div>
                    <code className="break-all text-text-secondary">{enrollData.secret}</code>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Dialog.Description className="mt-1 text-sm text-text-muted">
              Ingresa el código de 6 dígitos de tu app de autenticación.
            </Dialog.Description>
          )}

          <form
            className="mt-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (codeOk && !pending) onSubmit(code);
            }}
          >
            <div className="space-y-1">
              <label htmlFor="mfa-code" className="text-xs uppercase tracking-wide text-text-muted">
                Código de 6 dígitos
              </label>
              <Input
                id="mfa-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>

            {error && (
              <p role="alert" className="text-sm text-red">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!codeOk || pending}>
                {pending ? 'Verificando…' : 'Verificar'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
